import { forwardRef } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import type { colors } from "@eisox/tokens";

import { cn } from "../../index";
import { useCircleFontSize } from "../utils/useCircleFontSize";

const circleVariants = cva(
  `flex items-center justify-center rounded-full text-primary-white [&>svg]:block [&>svg]:h-[60%] [&>svg]:w-[60%]`,
  {
    variants: {
      backgroundColor: {
        white: "bg-primary-white",
        gray: "bg-primary-gray",
        darkerGray: "bg-primary-darker-gray",
        darkGray: "bg-primary-dark-gray",
        blue: "bg-primary-blue",
        darkBlue: "bg-primary-dark-blue",
        green: "bg-primary-green",
        red: "bg-primary-red",
        orange: "bg-primary-orange",
        warning: "bg-primary-warning",
      },
      color: {
        white: "text-primary-white",
        gray: "text-primary-gray",
        darkerGray: "text-primary-darker-gray",
        darkGray: "text-primary-dark-gray",
        blue: "text-primary-blue",
        darkBlue: "text-primary-dark-blue",
        green: "text-primary-green",
        red: "text-primary-red",
        orange: "text-primary-orange",
        warning: "text-primary-warning",
      },
    },
    defaultVariants: {
      backgroundColor: "blue",
      color: "white",
    },
  },
);

export interface CircleBaseProps
  extends React.HTMLAttributes<HTMLDivElement>, Omit<VariantProps<typeof circleVariants>, "backgroundColor" | "color"> {
  size?: number;
  backgroundColor?: keyof typeof colors.primary;
  color?: keyof typeof colors.primary;
}

const CircleBase = forwardRef<HTMLDivElement, CircleBaseProps>(
  ({ size = 40, backgroundColor, color, children, className, style, ...props }, ref) => {
    const fontSize = useCircleFontSize(size, children);

    return (
      <div
        {...props}
        ref={ref}
        className={cn(circleVariants({ backgroundColor, color }), className)}
        style={{
          ...style,
          width: size,
          height: size,
          fontSize: fontSize ?? style?.fontSize,
        }}
      >
        {children}
      </div>
    );
  },
);

CircleBase.displayName = "CircleBase";

export default CircleBase;
